import { useEffect } from "react";

const SITE_NAME = "RemboList";
const DEFAULT_IMAGE = "/social-preview.png";
const SITE_URL = "https://rembolist.co.ke";

function upsertMeta(attr, key, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prevContent = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, created, prevContent };
}

// Sets document.title plus the meta/OG/Twitter tags search engines and link
// previews read, for the life of the calling component — restoring whatever
// was there before on unmount so navigating between public pages never
// leaves a stale description behind. No react-helmet dependency: this app
// only has a handful of public routes, so direct DOM upserts (same pattern
// as useDocumentTitle) are enough.
export default function useSEO({ title, description, path, image, noindex } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = `${title} | ${SITE_NAME}`;

    const url = path ? `${SITE_URL}${path}` : undefined;
    const resolvedImage = image ? `${SITE_URL}${image}` : `${SITE_URL}${DEFAULT_IMAGE}`;

    const restorers = [
      upsertMeta("name", "description", description),
      upsertMeta("property", "og:title", title ? `${title} | ${SITE_NAME}` : undefined),
      upsertMeta("property", "og:description", description),
      upsertMeta("property", "og:url", url),
      upsertMeta("property", "og:image", resolvedImage),
      upsertMeta("property", "og:type", "website"),
      upsertMeta("name", "twitter:card", "summary_large_image"),
      upsertMeta("name", "twitter:title", title ? `${title} | ${SITE_NAME}` : undefined),
      upsertMeta("name", "twitter:description", description),
      upsertMeta("name", "twitter:image", resolvedImage),
      upsertMeta("name", "robots", noindex ? "noindex, nofollow" : undefined),
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      for (const { el, created, prevContent } of restorers) {
        if (created) el.remove();
        else el.setAttribute("content", prevContent ?? "");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, noindex]);
}
